import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  Lock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
  CreditCard,
  MessageSquare,
} from 'lucide-react';
import { Quote, Job } from '../../types';
import { initializeMarketplacePayment, checkJobPaymentStatus } from '../../lib/supabaseService';
import { useApp } from '../../context/AppContext';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface JobPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  quote: Quote;
  customerEmail?: string;
  customerName?: string;
  customerId?: string;
  onPaymentSuccess?: (reference: string) => void;
}

export const JobPaymentModal: React.FC<JobPaymentModalProps> = ({
  isOpen,
  onClose,
  job,
  quote,
  customerEmail,
  customerName,
  customerId,
}) => {
  const { currentUser, navigateTo } = useApp();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [paymentStatusCheck, setPaymentStatusCheck] = useState<{
    checking: boolean;
    isPaid: boolean;
    paymentStatus: string;
    canRetry: boolean;
    transaction?: any;
  }>({
    checking: true,
    isPaid: false,
    paymentStatus: 'none',
    canRetry: true,
  });

  useEffect(() => {
    if (isOpen && job?.id) {
      let isMounted = true;
      checkJobPaymentStatus(job.id).then((res) => {
        if (isMounted) {
          setPaymentStatusCheck({
            checking: false,
            isPaid: Boolean(res.isPaid),
            paymentStatus: res.paymentStatus || 'none',
            canRetry: res.canRetry ?? !res.isPaid,
            transaction: res.transaction,
          });
          if (res.isPaid) {
            const resolvedPaySt = res.paymentStatus === 'released' ? 'released' : 'held_in_escrow';
            if (job.status === 'open' || job.status === 'assigned') {
              job.status = resolvedPaySt === 'released' ? 'completed' : 'in_progress';
            }
            job.paymentStatus = resolvedPaySt;
            (job as any).payment_status = resolvedPaySt;
            job.escrow_held = resolvedPaySt !== 'released';
          }
        }
      });
      return () => {
        isMounted = false;
      };
    }
  }, [isOpen, job?.id]);

  const jobPayMeta = String(
    job?.paymentStatus ||
      (job as any)?.payment_status ||
      (job?.aiScopeEstimate as any)?.lifecycle_meta?.payment_status ||
      ''
  ).toLowerCase();

  const isAlreadyPaid = Boolean(
    paymentStatusCheck.isPaid ||
      job?.payment_released === true ||
      job?.escrow_held === true ||
      jobPayMeta === 'held_in_escrow' ||
      jobPayMeta === 'paid' ||
      jobPayMeta === 'released' ||
      job?.status === 'in_progress' ||
      job?.status === 'completed' ||
      job?.status === 'completed_by_professional' ||
      Boolean(job?.payment_reference || (job as any)?.paymentReference)
  );

  if (!isOpen) return null;

  // Derive authenticated customer email and ID
  const effectiveEmail = (currentUser?.email || customerEmail || '').trim();
  const effectiveCustomerId = currentUser?.id || customerId || job.customerId;
  const effectiveCustomerName = currentUser?.fullName || customerName || job.customerName;

  // Server authoritative amounts (preview)
  const grossAmount = quote.totalAmount;
  const platformFee = Math.round(grossAmount * 0.1);
  const professionalEarnings = grossAmount - platformFee;

  const handleStartPaystackPayment = async () => {
    if (isAlreadyPaid) {
      return;
    }
    setLoading(true);
    setError(null);

    // Validate email client-side first
    if (!effectiveEmail || !EMAIL_REGEX.test(effectiveEmail)) {
      setError('Your account email is missing or invalid. Please update your account email before making payment.');
      setLoading(false);
      return;
    }

    try {
      const originUrl = window.location.origin;
      const callbackUrl = `${originUrl}/api/marketplace/paystack/callback`;

      const res = await initializeMarketplacePayment({
        jobId: job.id,
        quoteId: quote.id,
        customerId: effectiveCustomerId,
        professionalId: quote.professionalId,
        grossAmount: grossAmount,
        customerEmail: effectiveEmail,
        customerName: effectiveCustomerName,
        callbackUrl,
      });

      if (res.success && res.authorization_url) {
        // Redirect to Paystack Checkout URL
        window.location.href = res.authorization_url;
      } else if (res.alreadyPaid) {
        job.status = 'in_progress';
        job.paymentStatus = 'held_in_escrow';
        (job as any).payment_status = 'held_in_escrow';
        job.escrow_held = true;
        setPaymentStatusCheck({
          checking: false,
          isPaid: true,
          paymentStatus: 'held_in_escrow',
          canRetry: false,
          transaction: res.transaction,
        });
        setLoading(false);
      } else {
        setError(res.error || 'Failed to initialize secure Paystack payment gateway.');
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Payment initialization error:', err);
      setError(err.message || 'An error occurred connecting to Paystack.');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-xs animate-fade-in">
      <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="bg-slate-900 text-white p-6 relative">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="absolute top-5 right-5 text-slate-400 hover:text-white transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold border border-emerald-500/30 mb-2">
            <ShieldCheck className="w-4 h-4" />
            <span>JobMatch NG Secure Escrow</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black">Confirm Your Job</h2>
          <p className="text-xs text-slate-300 mt-1">
            Funds are securely held in escrow until you confirm the work has been completed.
          </p>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5">
          {error && (
            <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl flex items-start gap-2.5 text-xs text-rose-800">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Job and Professional Details */}
          <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200/80 space-y-2.5 text-xs">
            <div className="flex justify-between items-start">
              <span className="text-slate-500 font-medium">Job Request:</span>
              <span className="font-bold text-slate-900 text-right max-w-[65%] truncate">
                {job.title}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500 font-medium">Professional:</span>
              <span className="font-bold text-slate-900">{quote.professionalName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500 font-medium">Location:</span>
              <span className="text-slate-700">{job.lga}, {job.state}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500 font-medium">Est. Completion:</span>
              <span className="text-slate-700">{quote.durationEstimate}</span>
            </div>
          </div>

          {/* Pricing Breakdown Breakdown */}
          <div className="rounded-2xl p-4.5 bg-emerald-50/50 border border-emerald-200/70 space-y-3">
            <div className="flex justify-between items-center text-xs">
              <span className="text-slate-600">Agreed Job Price:</span>
              <span className="font-bold text-slate-900 font-mono">
                ₦{grossAmount.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center text-xs text-slate-500 border-b border-emerald-200/50 pb-2">
              <span>JobMatch NG Service Fee:</span>
              <span className="font-mono text-slate-700">₦{platformFee.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center text-xs text-emerald-800 border-b border-emerald-200/50 pb-2">
              <span>Professional Earnings:</span>
              <span className="font-mono font-bold text-emerald-700">
                ₦{professionalEarnings.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center pt-1">
              <span className="text-sm font-extrabold text-slate-900">Total Payment:</span>
              <span className="text-xl sm:text-2xl font-black text-emerald-700 font-mono">
                ₦{grossAmount.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Escrow Terms Clarification */}
          <div className="rounded-xl p-3.5 bg-amber-50 border border-amber-200/80 text-xs text-amber-900 space-y-1.5 leading-relaxed">
            <div className="flex items-center gap-1.5 font-bold">
              <Lock className="w-3.5 h-3.5 text-amber-700 shrink-0" />
              <span>Escrow Security Commitment</span>
            </div>
            <p className="text-[11px] text-amber-800">
              "Your payment will be securely held while the professional completes the job.
              The professional's earnings will be released after you confirm that the work has been completed,
              subject to JobMatch NG's payment and dispute rules."
            </p>
          </div>

          {/* Previous Payment Status Notice */}
          {!isAlreadyPaid && (paymentStatusCheck.paymentStatus === 'failed' || paymentStatusCheck.paymentStatus === 'abandoned') && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span>
                Previous payment attempt was not completed or failed. You can safely initiate a new payment attempt below.
              </span>
            </div>
          )}

          {!isAlreadyPaid && paymentStatusCheck.paymentStatus === 'refunded' && (
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-xl text-xs text-purple-900 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
              <span>
                Previous escrow payment for this job was refunded. A new payment is required to proceed with this professional.
              </span>
            </div>
          )}

          {/* Payment CTA or Duplicate Payment Safeguard */}
          <div className="space-y-2 pt-2">
            {isAlreadyPaid ? (
              <div className="p-4 rounded-2xl bg-blue-50 border border-blue-200 text-xs text-blue-900 space-y-3">
                <div className="flex items-center gap-2 font-bold text-sm text-blue-950">
                  <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span>Escrow Payment Already Secured</span>
                </div>
                <p className="text-slate-600">
                  Payment for this job is already secured in escrow and your job is in Active Jobs. You cannot make a duplicate payment.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigateTo('customer_dashboard', {
                        tab: 'active',
                        activeTab: 'active',
                        jobId: job.id,
                        proId: quote.professionalId,
                      });
                    }}
                    className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer min-h-[44px]"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>View in Active Jobs</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigateTo('messages', { jobId: job.id, proId: quote.professionalId });
                    }}
                    className="flex-1 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer min-h-[44px]"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>Go to Job Chat</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleStartPaystackPayment}
                  disabled={loading}
                  className="w-full py-3.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-sm shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Connecting to Paystack...</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      <span>Accept Quote & Pay ₦{grossAmount.toLocaleString()}</span>
                    </>
                  )}
                </button>
                <p className="text-center text-[10px] text-slate-400">
                  Secured 256-bit SSL Paystack payment. Cards, USSD, Bank Transfer, and QR supported.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
